import React, { useEffect, useState } from "react";
import { useLocation, useHistory, useParams } from "react-router-dom";
import moment from "moment";
import { t } from "i18next";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Flex, Text } from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import ESelect from "components/ESelect";
import ECheckbox from "components/ECheckbox";
import ERadioGroup from "components/ERadioGroup";
import PrimaryButton from "components/Buttons/PrimaryButton";
import SecondaryButton from "components/Buttons/SecondaryButton";
import EDateRangePicker from "components/EDateRangePicker";
import {
  RADIO_DATA_VALID_PERIOD,
  RADIO_DATA_TARGET_RENEWAL_CYCLE,
  PATHNAME,
} from "constants";
import DeleteButton from "components/Buttons/DeleteButton";
import { useModalState } from "hooks/useModalState";
import ModalEmployees from "components/modals/ModalEmployees";
import ESelectWithSearch from "components/ESelectWithSearch";
import { useGetListDocument } from "hooks/useDocument";
import ModalAlert from "components/modals/AlertDialog";
import {
  useUpdateCourse,
  usePushJobManual,
  useDeleteCourse,
  getDetailCourse,
  useCreateCourse,
} from "hooks/useCourse";
import {
  OPTIONS_TARGET_GROUP,
  DOCUMENT_TYPE_OVERALL_TRAINING,
  FORMAT_DATE,
  FORMAT_DATE_PLACEHOLDER,
} from "constants";
import GreenButton from "components/Buttons/GreenButton";
import DisableButton from "../../components/Buttons/DisableButton";
import useSearchEmployee from "hooks/useSearchEmployee";
import { useEmployeeStore } from "store/employeeStore";
import CourseEmployeeInfo from "./CourseEmployeeInfo";
import { getExceptionEmployees } from "utils";

const CourseForm = () => {
  const location = useLocation();
  const isCreatePage = location.pathname === PATHNAME.CREATE_COURSE;
  const history = useHistory();
  const { courseId } = useParams();

  const { modalState, open, close } = useModalState({
    modalEmployees: false,
    modalAlert: false,
  });

  const [option, setOption] = useState([]);
  // const [tempDocument, setTempDocument] = useState();
  // const [isClearDocument, setIsClearDocument] = useState();

  const { data: dataCourse } = getDetailCourse(courseId, true);
  const { data: dataDocuments } = useGetListDocument({ page: 1 });

  const schema = yup.object().shape({
    document_id: yup.object().nullable(),
    target_group_id: yup.string().required(t("messages.required")),
    validity_from: yup.string().nullable(),
    validity_to: yup
      .string()
      .nullable()
      .test("validity_from", t("messages.date_end"), (value, context) => {
        return !(
          value && new Date(value) < new Date(context.parent.validity_from)
        );
      }),
  });
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    reset,
    setError,
    clearErrors,
  } = useForm({
    defaultValues: {
      target_group_id: "",
      is_required_all: false,
      effective_time: "1",
      validity_from: "",
      validity_to: "",
      employees: "",
      target_renewal_cycle: "1",
      document_id: null,
    },
    resolver: yupResolver(schema),
  });

  const {
    onSubmit: onSearchEmployee,
    fetchAllDataEmployees,
    handleSelectAllEmployee,
  } = useSearchEmployee(
    {
      is_open: modalState.modalEmployees,
    },
    setValue
  );

  const {
    setParamsListEmployee,
    setIsSelectedAllEmployee,
    setSelectedEmployeeIds,
    resetAllEmployee,
  } = useEmployeeStore();
  const {
    allEmployees,
    paramsListEmployee,
    selectedEmployeeIds,
  } = useEmployeeStore((state) => state);

  const mutationCreateCourse = useCreateCourse();
  const mutationUpdateCourse = useUpdateCourse();
  const mutationDeleteCourse = useDeleteCourse();
  const mutationPushJobManual = usePushJobManual();

  useEffect(() => {
    resetAllEmployee();
    fetchAllDataEmployees();
  }, []);

  const onPushManual = (id) => {
    if (getValues("document_id")) {
      mutationPushJobManual.mutate({ id: id });
      clearErrors("document_id");
      return;
    }
    setError("document_id", { message: t("messages.required") });
  };

  const closeModal = () => {
    close("modalEmployees");
  };

  const onchangeAllCompany = (event) => {
    handleSelectAllEmployee(event.target.checked);

    if (event.target.checked) {
      setValue("employees", t("pages.course_form.all_people"));
    } else {
      setValue("employees", []);
    }
  };

  useEffect(() => {
    if (dataDocuments?.result?.data) {
      setOption(
        [{ label: " 選択して下さい", value: "" }].concat(
          dataDocuments?.result?.data
            ?.filter((itm) => {
              return itm.type === DOCUMENT_TYPE_OVERALL_TRAINING;
            })
            .map((item) => {
              return {
                value: item.id,
                label: `${item.course_code}  ${item.name}`,
              };
            })
        )
      );
    }
  }, [dataDocuments?.result?.data]);

  //set value to form when dataCourse is fetched
  useEffect(() => {
    if (dataCourse?.result?.data && !isCreatePage) {
      const detailCourse = dataCourse.result.data;
      const selectedUser = JSON.parse(detailCourse?.user_selected_list);

      if (!detailCourse?.is_required_all) {
        setSelectedEmployeeIds(selectedUser.map((item) => item.id));
        setParamsListEmployee({
          ...paramsListEmployee,
          id_selected_list: JSON.stringify(selectedUser.map((item) => item.id)),
          id_exception_list: null,
          no_data: 0,
          isPaging: 0,
        });
      }

      if (detailCourse?.is_required_all) {
        setParamsListEmployee({
          ...paramsListEmployee,
          id_selected_list: null,
          id_exception_list: JSON.stringify(
            selectedUser.map((item) => item.id)
          ),
          no_data: 0,
          isPaging: 0,
        });
        // In case is_required_all = true, user_selected_list is store exception employees
        const exceptionEmployeeIds = JSON.parse(detailCourse?.user_selected_list).map((i) => i.id);
        setSelectedEmployeeIds(
          allEmployees
            .filter((i) => !exceptionEmployeeIds.includes(i.id))
            .map((i) => i.id)
        );
      }
      reset({
        target_group_id: detailCourse?.target_group_id,
        is_required_all: !!detailCourse?.is_required_all,
        // name: detailCourse?.name,
        // description: detailCourse?.description,
        effective_time: detailCourse?.effective_time?.toString(),
        validity_from: detailCourse?.validity_from
          ? new Date(detailCourse?.validity_from)
          : null,
        validity_to: detailCourse?.validity_to
          ? new Date(detailCourse?.validity_to)
          : null,
        user_selected_list: detailCourse?.user_selected_list,
        employees: JSON.parse(detailCourse?.user_selected_list)
          .map((i) => i.name)
          .join(", "),
        target_renewal_cycle: detailCourse?.target_renewal_cycle?.toString(),
        document_id: option?.find((i) => {
          if (i) return i.value === detailCourse.document_id;
        }),
      });
      // setTempDocument(
      //   option?.find((i) => {
      //     if (i) return i.value === detailCourse.document_id;
      //   })
      // );
    }
  }, [dataCourse?.result?.data, option, allEmployees]);

  const onSubmit = async (data) => {
    if (!data.is_required_all) {
      data.employees = selectedEmployeeIds;
    }
    const payload = {
      target_group_id: data.target_group_id,
      is_required_all: +data.is_required_all,
      effective_time: data.effective_time,
      target_renewal_cycle: data.target_renewal_cycle,
      document_id:
        data.document_id && data.document_id.value
          ? data.document_id.value
          : null,
      sub_list: [],
    };

    if (data.effective_time == 2) {
      payload.validity_from = moment(data.validity_from).isValid()
        ? moment(data.validity_from).format("YYYY-MM-DD")
        : "";
      payload.validity_to = moment(data.validity_to).isValid()
        ? moment(data.validity_to).format("YYYY-MM-DD")
        : "";
    }

    if (!data.is_required_all) {
      payload.user_selected_list = selectedEmployeeIds;
      payload.sub_list = [];
    } else {
      const exceptionList = getExceptionEmployees(
        allEmployees,
        selectedEmployeeIds
      );
      payload.sub_list = exceptionList.map((i) => i.id);
      payload.user_selected_list = [];
    }

    if (isCreatePage) {
      mutationCreateCourse.mutate(payload);
      return;
    }

    mutationUpdateCourse.mutate({ ...payload, id: courseId });
  };

  const handelCourseChange = (data) => {
    setValue("document_id", data);
    // if (data?.value) {
    //   setIsClearDocument(false);
    // }
  };

  useEffect(() => {
    setIsSelectedAllEmployee(watch("is_required_all"));
  }, [watch("is_required_all")]);;

  return (
    <Flex direction="column" pt={{ base: "120px", md: "40px" }}>
      <Card overflowX={"hidden"} pb="0px">
        <CardHeader p="6px 0px 20px 0px">
          <Flex justifyContent={"space-between"}>
            <Text fontSize="xl" fontWeight="bold">
              {t("pages.course_form.title_create")}
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex gap={"10px"} align={"center"} mb={"10px"}>
              <Box w={"15%"}>
                <Text fontSize={"14px"}>
                  {t("pages.course_form.label.position_group")}
                </Text>
              </Box>
              <Box width={"50%"}>
                <ESelect
                  name="target_group_id"
                  placeholder={t("common.option.placeholder")}
                  control={control}
                  dataSelect={OPTIONS_TARGET_GROUP.map((option) => {
                    return { id: option.id, name: t(option.name) };
                  })}
                />
              </Box>
            </Flex>

            <Flex gap={"10px"} mb={"10px"}>
              <Box w={"15%"}>
                <Text fontSize={"14px"}>
                  {t("pages.course_form.label.document")}
                </Text>
              </Box>
              <Box width={"50%"}>
                <ESelectWithSearch
                  name="document_id"
                  control={control}
                  options={option}
                  placeholder={t("pages.course_form.document_placeholder")}
                  onChange={handelCourseChange}
                />
              </Box>
            </Flex>

            <Flex gap={"10px"} mb={"10px"} mt={"20px"}>
              <Box w={"15%"} mt={"8px"}>
                <Text fontSize={"14px"}>
                  {t("pages.course_form.label.valid_time")}
                </Text>
              </Box>
              <Flex width={"50%"}>
                <Box w={"150px"} mt={"8px"}>
                  <ERadioGroup
                    name="effective_time"
                    control={control}
                    radioData={RADIO_DATA_VALID_PERIOD}
                  />
                </Box>
                <Box ml={"10px"} w={"40%"}>
                  <EDateRangePicker
                    disabled={watch("effective_time") !== "2"}
                    setValue={setValue}
                    control={control}
                    name="validity_from"
                    getValues={getValues}
                    reset={reset}
                    placeholder={FORMAT_DATE_PLACEHOLDER}
                    watch={watch}
                    dateFormat={FORMAT_DATE}
                  />
                </Box>
                <Box ml={"10px"} mt={"8px"}>
                  ~
                </Box>
                <Box ml={"10px"} w={"40%"}>
                  <EDateRangePicker
                    disabled={watch("effective_time") !== "2"}
                    setValue={setValue}
                    name="validity_to"
                    control={control}
                    getValues={getValues}
                    reset={reset}
                    placeholder={FORMAT_DATE_PLACEHOLDER}
                    watch={watch}
                    dateFormat={FORMAT_DATE}
                  />
                </Box>
              </Flex>
            </Flex>

            <Flex gap={"10px"} align={"center"} mb={"10px"}>
              <Box w={"15%"}>
                <Text fontSize={"14px"}>
                  {t("pages.course_form.label.required_all")}
                </Text>
              </Box>
              <Box width={"50px"} mt={"10px"}>
                <ECheckbox
                  name="is_required_all"
                  control={control}
                  onChange={onchangeAllCompany}
                />
              </Box>
              {/* <Flex gap={'10px'} align={'center'} mb={'10px'}> */}
              <Box w={"100px"}>
                <Text fontSize={"14px"}>
                  {t("pages.course_form.label.renew")}
                </Text>
              </Box>
              <Box width={"30%"}>
                <ERadioGroup
                  name="target_renewal_cycle"
                  placeholder=""
                  control={control}
                  radioData={RADIO_DATA_TARGET_RENEWAL_CYCLE}
                />
              </Box>
              {/* </Flex> */}
            </Flex>

            <CourseEmployeeInfo
              handleFirstBtn={() => open("modalEmployees")}
              textFirstBtn={t("pages.course_form.member_confirmation")}
              isSelectedAll={watch("is_required_all")}
            />

            <Flex justifyContent={"end"} gap={"20px"} pb={"10px"}>
              {isCreatePage ? (
                <PrimaryButton
                  text={t("pages.course_form.register")}
                  type="submit"
                  isLoading={mutationCreateCourse.isLoading}
                />
              ) : (
                <Flex gap={"20px"}>
                  {dataCourse?.result?.data?.campus_status == 2 ? (
                    <DisableButton
                      text={t("pages.course_form.save_changes")}
                      type="submit"
                      isLoading={mutationUpdateCourse.isLoading}
                    />
                  ) : (
                    <PrimaryButton
                      text={t("pages.course_form.save_changes")}
                      type="submit"
                      isLoading={mutationUpdateCourse.isLoading}
                    />
                  )}
                  <DeleteButton
                    text={t("pages.course_form.un_assign_course")}
                    onClick={() => open("modalAlert")}
                  />
                </Flex>
              )}
              <SecondaryButton
                text={t("pages.course_form.cancel")}
                onClick={() => history.go(-1)}
              />
            </Flex>
          </form>
          {!isCreatePage && (
            <Flex justifyContent={"center"} pb={"20px"}>
              {dataCourse?.result?.data?.campus_status == 2 ? (
                <DisableButton
                  text={t("pages.course_form.pushing")}
                  isLoading={"true"}
                />
              ) : (
                <GreenButton
                  text={t("pages.course_form.cloud_campus_status_1")}
                  onClick={() => onPushManual(courseId)}
                  disabled={dataCourse?.result?.data?.campus_status === 4}
                />
              )}
            </Flex>
          )}
        </CardBody>
      </Card>
      {modalState.modalEmployees && (
        <ModalEmployees
          isOpen={modalState.modalEmployees}
          onClose={closeModal}
          editMode={!!courseId}
          setParentFormValue={setValue}
          onSubmit={onSearchEmployee}
        />
      )}
      <ModalAlert
        isOpen={modalState.modalAlert}
        onClose={() => close("modalAlert")}
        title={t("pages.course_form.delete_course")}
        content={t("pages.course_form.confirm_delete_course")}
        textBtnLeft={t("pages.course_form.cancel")}
        textBtnRight={t("pages.course_form.delete")}
        onClickBtnLeft={() => close("modalAlert")}
        onClickBtnRight={() => mutationDeleteCourse.mutate(courseId)}
      />
    </Flex>
  );
};

export default CourseForm;
